import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import axios from '../utils/api';
import { useNotifications } from '../contexts/NotificationContext';

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  });
};

const BuyerNotificationsPage = () => {
  const navigate = useNavigate();
  const { refreshNotificationCount } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/notifications');
      const payload = response.data || {};
      setUnreadCount(Number(payload.unread_count || 0));
      setNotifications(Array.isArray(payload.notifications) ? payload.notifications : []);
      await refreshNotificationCount();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRead = async (notification) => {
    try {
      setUpdatingId(String(notification.id));
      setError('');
      await axios.patch(`/api/notifications/${notification.id}/read`, {
        is_read: !notification.is_read,
      });
      await loadNotifications();
      setSuccess(notification.is_read ? 'Marked as unread' : 'Marked as read');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update notification');
    } finally {
      setUpdatingId('');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      setUpdatingId(String(notificationId));
      setError('');
      await axios.delete(`/api/notifications/${notificationId}`);
      await loadNotifications();
      setSuccess('Notification deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete notification');
    } finally {
      setUpdatingId('');
    }
  };

  const handleActionClick = (notification) => {
    if (!notification.action_url) {
      return;
    }

    if (notification.action_url.startsWith('http://') || notification.action_url.startsWith('https://')) {
      window.open(notification.action_url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(notification.action_url);
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h2 className="h4 mb-1">My Notifications</h2>
                  <p className="text-muted mb-0">Unread: <strong>{unreadCount}</strong></p>
                </div>
                <Button variant="outline-primary" onClick={loadNotifications} disabled={loading}>
                  Refresh
                </Button>
              </div>
            </Card.Body>
          </Card>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : notifications.length === 0 ? (
            <Alert variant="info">You have no notifications.</Alert>
          ) : (
            notifications.map((notification) => (
              <Card key={notification.id} className="mb-3 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h3 className="h6 mb-0">{notification.title}</h3>
                        {!notification.is_read && <Badge bg="danger">Unread</Badge>}
                        {notification.source === 'admin' && <Badge bg="primary">Admin</Badge>}
                      </div>
                      <p className="mb-1">{notification.message}</p>
                      <small className="text-muted">{formatDateTime(notification.created_at)}</small>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {notification.action_url && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleActionClick(notification)}
                        >
                          {notification.action_label || 'Open'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={updatingId === String(notification.id)}
                        onClick={() => toggleRead(notification)}
                      >
                        {notification.is_read ? 'Mark Unread' : 'Mark Read'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={updatingId === String(notification.id)}
                        onClick={() => deleteNotification(notification.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default BuyerNotificationsPage;
